<template>
  <WithBackdrop
    :class="[$style.config, $style.wrap]"
    :backdrop="data?.fields?.backdrop"
    data-flexible-panels-v="3.0.1"
  >
    <BaseContainer :max-width="640">
      <div :class="$style.leadingContent">
        <BaseHeading v-if="computedHeader" size="h2" :class="$style.header">
          <span v-html="computedHeader" />
        </BaseHeading>

        <MarkedParser :md="computedCopy" :class="$style.copy" />
        <div :class="$style.ctas">
          <CallToAction
            v-for="cta in computedCtas"
            :key="cta?.sys?.id"
            :cta="cta"
            :class="$style.cta"
          />
        </div>
      </div>
    </BaseContainer>

    <BaseContainer :max-width="1280">
      <div :class="$style.grid">
        <div
          v-for="(panel, index) in computedPanelReferences"
          :key="panel?.sys.id"
          :class="$style.panelCardWrap"
          :data-panel-index="index"
        >
          <BaseCard :class="$style.panelCard">
            <template #body>
              <MarkedParser :md="panel?.fields?.copy" />
            </template>
            <template #actions>
              <CallToAction
                v-for="cta in panel?.fields?.ctas"
                :key="cta?.sys?.id"
                :cta="cta"
              />
            </template>
          </BaseCard>
        </div>
      </div>
    </BaseContainer>
  </WithBackdrop>
</template>

<script setup lang="ts">
import type { TypeFlexibleContentPanelsWithoutUnresolvableLinksResponse } from '~/types/contentful'
import { instanceOfCard } from '~/utils/instanceOfEntry'

const props = defineProps<{
  data: TypeFlexibleContentPanelsWithoutUnresolvableLinksResponse
}>()

const { $visibility } = useNuxtApp()

const computedHeader = computed(() => {
  return props.data?.fields?.header || ''
})

const computedCopy = computed(() => {
  return props.data?.fields?.copy || ''
})

const computedCtas = computed(() => {
  return props.data?.fields?.callToActions || []
})

const computedPanelReferences = computed(
  () =>
    props.data?.fields?.panelReferences?.filter?.(
      (panel) =>
        instanceOfCard(panel) && $visibility(panel?.fields?.visibility),
    ) || [],
)
</script>

<style lang="scss" module>
.config {
  @include breakpoint-variables(
    '--flexible-content-panels-wrap-padding',
    (
      base: 30px 15px,
      sm: 40px 15px,
      md: 60px 30px,
      lg: 60px 30px,
    )
  );

  @include breakpoint-variables(
    '--flexible-content-panels-grid-template-columns',
    (
      base: '1fr',
      sm: 'repeat(2, 1fr)',
      md: 'repeat(2, 1fr)',
      lg: 'repeat(6, 1fr)',
    )
  );
}

.wrap {
  color: var(--flexible-content-panels-text-color, inherit);
  display: flex;
  flex-direction: column;
  padding: var(--flexible-content-panels-wrap-padding);
}

.leadingContent {
  text-align: var(--flexible-content-panels-leading-text-align, center);
  display: flex;
  flex-direction: column;
  gap: 12px;

  .header {
    line-height: 0.85em;
  }

  .ctas {
    display: flex;
    flex-direction: row;
    justify-content: center;
    gap: 12px;
    flex-wrap: wrap;
    margin: 6px;
  }
}

.grid {
  display: grid;
  grid-template-columns: var(--flexible-content-panels-grid-template-columns);

  gap: var(--flexible-content-panels-grid-gap, 12px);
  margin-top: var(--flexible-content-panels-grid-margin-top, 40px);
  margin-bottom: var(--flexible-content-panels-grid-margin-bottom, 40px);

  @include media('lg') {
    /* Default: assume 3-up row */
    & > .panelCardWrap {
      grid-column: span 2;
    }

    /* Every group of 5 items → first two are 2-up */
    // & > .panelCardWrap:nth-child(5n + 1),
    // & > .panelCardWrap:nth-child(5n + 2) {
    //   grid-column: span 3;
    // }

    // requseted to change to 2 columns for all items in the 6 column layout Feb 11, 2026
    & > .panelCardWrap {
      grid-column: span 3;
    }
  }
}

.panelCardWrap {
  border-radius: var(--flexible-content-panel-border-radius, 8px);
  border: var(--flexible-content-panel-border, none);
  box-shadow: 0 4px 13px rgba(0, 0, 0, 0.06);
}
</style>
